import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Flex, Grid, Loader, Tabs, Text, Title, useMantineColorScheme } from "@mantine/core";
import { ClientUserRole } from "src/graphql/generated/graphql";

import { ClientAPI, ClientBilling, ClientSettings, ClientUsers } from "../../components/ClientDetail";
import { useAuthContext } from "../../hooks/auth";
import { useClient } from "../../hooks/clients";

const defaultTab = "settings";

export interface OrganizationPageProps {}
export const OrganizationPage = ({}: OrganizationPageProps) => {
    const { t } = useTranslation(["common"]);

    const params = useParams();
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();

    const tab = params?.tab || defaultTab;

    const { client: selectedClient } = useAuthContext();

    const { client, isLoadingClient, reloadClient } = useClient(selectedClient?.id);

    const isAdmin = selectedClient?.role === ClientUserRole.Admin;
    const isOwner = selectedClient?.role === ClientUserRole.Owner;
    const isDeveloper = selectedClient?.role === ClientUserRole.Developer;

    return (
        <>
            <Flex
                align="center"
                justify="space-between"
                mb="lg"
                pb="md"
                rowGap={8}
                wrap="wrap"
                style={{
                    borderBottom: "1px solid #E0E0E0",
                }}
            >
                <Flex gap={16} align="center">
                    <Title order={3} style={{ fontWeight: 600 }}>
                        {isLoadingClient ? <Loader /> : client?.name}
                    </Title>
                </Flex>
            </Flex>
            <Tabs
                styles={{
                    tab: {
                        paddingInline: 12,
                        paddingBlock: 8,
                    },
                }}
                variant="pills"
                orientation="vertical"
                color={colorScheme === "dark" ? "orange" : "gray"}
                keepMounted={false}
                value={tab}
                onTabChange={(tab) => navigate(`/organization${tab === defaultTab ? "" : `/${tab}`}`)}
            >
                <Grid w="100%" gutter={0}>
                    <Grid.Col md="content" pr={20} pb={20}>
                        <Tabs.List mb={24}>
                            <Tabs.Tab value="settings">
                                <Text size={14} weight={600}>
                                    {t("client_settings")}
                                </Text>
                            </Tabs.Tab>
                            {isOwner ||
                                (isAdmin && (
                                    <Tabs.Tab value="billing">
                                        <Text size={14} weight={600}>
                                            {t("billing_details")}
                                        </Text>
                                    </Tabs.Tab>
                                ))}
                            {isOwner ||
                                (isAdmin && (
                                    <Tabs.Tab value="users">
                                        <Text size={14} weight={600}>
                                            {t("client_users")}
                                        </Text>
                                    </Tabs.Tab>
                                ))}
                            {isOwner ||
                                isAdmin ||
                                (isDeveloper && (
                                    <Tabs.Tab value="api">
                                        <Text size={14} weight={600}>
                                            {t("api_keys")}
                                        </Text>
                                    </Tabs.Tab>
                                ))}
                        </Tabs.List>
                    </Grid.Col>
                    <Grid.Col md="auto">
                        <Container size="sm" p={0}>
                            <Tabs.Panel value="settings">
                                <ClientSettings
                                    client={client}
                                    isLoading={!selectedClient?.id || isLoadingClient}
                                    onClientUpdate={reloadClient}
                                />
                            </Tabs.Panel>
                            {isOwner ||
                                (isAdmin && (
                                    <Tabs.Panel value="billing">
                                        <ClientBilling
                                            client={client}
                                            isLoading={!selectedClient?.id || isLoadingClient}
                                            onClientUpdate={reloadClient}
                                        />
                                    </Tabs.Panel>
                                ))}
                            {isOwner ||
                                (isAdmin && (
                                    <Tabs.Panel value="users">
                                        <ClientUsers client={client} />
                                    </Tabs.Panel>
                                ))}
                            {isOwner ||
                                isAdmin ||
                                (isDeveloper && (
                                    <Tabs.Panel value="api">
                                        <ClientAPI client={client} />
                                    </Tabs.Panel>
                                ))}
                        </Container>
                    </Grid.Col>
                </Grid>
            </Tabs>
        </>
    );
};
